<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatClockTime } from '@/filters/dateTimeFilters';
import { useAuthStore } from '@/stores/auth';
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: null,
    },
    label: {
        type: String,
        default: 'Time',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    dataCy: {
        type: String,
        required: true,
    },
    // Every minute, so nothing a caller has to record can be unreachable. A caller whose times
    // land on the clock rather than between it - a schedule being written - steps up from here.
    minuteStep: {
        type: Number,
        default: 1,
    },
});

// `invalid` is emitted, not merely rendered: an unparseable entry leaves the model on its
// previous value, so a host that cannot see the invalid state would save a time the field is
// not showing.
const emit = defineEmits(['update:modelValue', 'update:invalid']);

const authStore = useAuthStore();

// The value is always a 24-hour 'HH:mm'; only the reading of it follows the account. Note
// HH rather than the store's kk, which renders midnight as 24:00 — wrong on a MAR.
const militaryTime = computed(() => Boolean(authStore.militaryTime));

const placeholder = computed(() => (militaryTime.value ? 'HH:mm' : 'h:mm AM'));

const display = (value) => (value ? formatClockTime(value) : '');

// 8 | 8:00 | 08:00 | 0800 | 8pm | 8:00 PM — a bare wall-clock time with no date part.
const TIME_INPUT = /^(\d{1,2})[:.]?(\d{2})?\s*([ap])\.?m?\.?$|^(\d{1,2})[:.]?(\d{2})?$/i;

const text = ref('');
const invalid = ref(false);

const pad = (value) => String(value).padStart(2, '0');

/**
 * @returns {string|null|undefined} 'HH:mm', null when blank, undefined when unparseable.
 */
const normalize = (raw) => {
    const value = String(raw ?? '').trim();
    if (!value) return null;

    const match = TIME_INPUT.exec(value);
    if (!match) return undefined;

    const meridiem = match[3]?.toLowerCase();
    let hours = Number(meridiem ? match[1] : match[4]);
    const minutes = Number((meridiem ? match[2] : match[5]) ?? 0);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes > 59) return undefined;

    if (meridiem) {
        if (hours < 1 || hours > 12) return undefined;
        if (meridiem === 'p' && hours < 12) hours += 12;
        if (meridiem === 'a' && hours === 12) hours = 0;
    } else if (hours > 23) {
        return undefined;
    }

    return `${pad(hours)}:${pad(minutes)}`;
};

const setInvalid = (value) => {
    if (invalid.value === value) return;
    invalid.value = value;
    emit('update:invalid', value);
};

const commit = () => {
    const normalized = normalize(text.value);
    if (normalized === undefined) {
        setInvalid(true);
        return;
    }
    setInvalid(false);
    text.value = display(normalized);
    if (normalized !== (props.modelValue || null)) emit('update:modelValue', normalized);
};

watch(
    [() => props.modelValue, militaryTime],
    ([value]) => {
        text.value = display(value);
        setInvalid(false);
    },
    { immediate: true },
);

// 12 leads each half the way a clock reads — 12, 1, 2 … 11 — unless the account runs on a
// 24-hour clock, which has no meridiem to choose either.
const hourOptions = computed(() => (militaryTime.value ? Array.from({ length: 24 }, (item, index) => pad(index)) : Array.from({ length: 12 }, (item, index) => pad(index === 0 ? 12 : index))));

const minuteOptions = computed(() => {
    const step = Number(props.minuteStep) > 0 ? Number(props.minuteStep) : 1;
    return Array.from({ length: Math.ceil(60 / step) }, (item, index) => pad(index * step));
});

const MERIDIEMS = ['AM', 'PM'];

const menu = ref(false);
const draftHour = ref(null);
const draftMinute = ref('00');
const draftMeridiem = ref('AM');
const columnRefs = ref({});

const setColumnRef = (name) => (element) => {
    columnRefs.value[name] = element;
};

/** Split 'HH:mm' into the three columns. */
const seedDraft = () => {
    const current = normalize(text.value) || props.modelValue;
    if (!current) {
        draftHour.value = null;
        draftMinute.value = '00';
        draftMeridiem.value = 'AM';
        return;
    }
    const [hours, minutes] = current.split(':').map(Number);
    draftMeridiem.value = hours >= 12 ? 'PM' : 'AM';
    draftHour.value = militaryTime.value ? pad(hours) : pad(hours % 12 === 0 ? 12 : hours % 12);
    // A stored time off the step still has to show as selected, so the column is not
    // silently rounded out from under the user.
    draftMinute.value = pad(minutes);
};

/** Bring the chosen row into view — twelve hours do not fit the column at once. */
const scrollToSelection = () => {
    Object.values(columnRefs.value).forEach((column) => {
        const selected = column?.$el?.querySelector?.('.bn-time-option--selected') || column?.querySelector?.('.bn-time-option--selected');
        if (selected) selected.scrollIntoView({ block: 'center' });
    });
};

// Opened by the field itself as well as by the clock beside it: the whole control is one target,
// and hunting a 20px icon at a cart is the reason it was not being used. The icon's own handler
// stops propagation, so a click on it does not arrive here twice.
const openMenu = () => {
    if (props.disabled) return;
    seedDraft();
    menu.value = true;
    nextTick(scrollToSelection);
};

const canApply = computed(() => Boolean(draftHour.value));

const apply = () => {
    if (!canApply.value) return;
    let hours = Number(draftHour.value);
    if (!militaryTime.value) {
        hours %= 12;
        if (draftMeridiem.value === 'PM') hours += 12;
    }
    const value = `${pad(hours)}:${draftMinute.value}`;
    text.value = display(value);
    setInvalid(false);
    menu.value = false;
    if (value !== (props.modelValue || null)) emit('update:modelValue', value);
};

const cancel = () => {
    menu.value = false;
};

// A minute already stored off the step is offered alongside the steps rather than dropped.
const minuteColumn = computed(() => {
    const options = minuteOptions.value;
    if (draftMinute.value && !options.includes(draftMinute.value)) return [...options, draftMinute.value].sort();
    return options;
});
</script>

<template>
    <v-text-field
        v-model="text"
        :label="props.label"
        :disabled="props.disabled"
        :error-messages="invalid ? `Enter a time such as ${placeholder}` : ''"
        :placeholder="placeholder"
        density="compact"
        :data-cy="props.dataCy"
        @click="openMenu"
        @blur="commit"
        @keyup.enter="commit"
    >
        <template #append-inner>
            <v-menu v-model="menu" :close-on-content-click="false" location="bottom end" :disabled="props.disabled">
                <template #activator="{ props: activatorProps }">
                    <v-btn
                        v-bind="activatorProps"
                        :disabled="props.disabled"
                        :data-cy="`${props.dataCy}Open`"
                        aria-label="Choose a time"
                        variant="text"
                        density="comfortable"
                        size="small"
                        icon
                        @click.stop="openMenu"
                    >
                        <FontAwesomeIcon :icon="['far', 'clock']" />
                    </v-btn>
                </template>

                <v-card :data-cy="`${props.dataCy}Menu`" min-width="260">
                    <div class="d-flex">
                        <v-list :ref="setColumnRef('hour')" density="compact" class="bn-time-column py-0" :data-cy="`${props.dataCy}Hours`">
                            <v-list-item
                                v-for="hour in hourOptions"
                                :key="hour"
                                :class="['bn-time-option', { 'bn-time-option--selected': hour === draftHour }]"
                                :active="hour === draftHour"
                                :data-cy="`${props.dataCy}Hour${hour}`"
                                @click="draftHour = hour"
                            >
                                <v-list-item-title class="text-center">{{ hour }}</v-list-item-title>
                            </v-list-item>
                        </v-list>

                        <v-divider vertical></v-divider>

                        <v-list :ref="setColumnRef('minute')" density="compact" class="bn-time-column py-0" :data-cy="`${props.dataCy}Minutes`">
                            <v-list-item
                                v-for="minute in minuteColumn"
                                :key="minute"
                                :class="['bn-time-option', { 'bn-time-option--selected': minute === draftMinute }]"
                                :active="minute === draftMinute"
                                :data-cy="`${props.dataCy}Minute${minute}`"
                                @click="draftMinute = minute"
                            >
                                <v-list-item-title class="text-center">{{ minute }}</v-list-item-title>
                            </v-list-item>
                        </v-list>

                        <v-divider v-if="!militaryTime" vertical></v-divider>

                        <v-list v-if="!militaryTime" density="compact" class="bn-time-column py-0" :data-cy="`${props.dataCy}Meridiems`">
                            <v-list-item
                                v-for="meridiem in MERIDIEMS"
                                :key="meridiem"
                                :class="['bn-time-option', { 'bn-time-option--selected': meridiem === draftMeridiem }]"
                                :active="meridiem === draftMeridiem"
                                :data-cy="`${props.dataCy}${meridiem}`"
                                @click="draftMeridiem = meridiem"
                            >
                                <v-list-item-title class="text-center">{{ meridiem }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </div>

                    <v-divider></v-divider>

                    <v-card-actions class="justify-end">
                        <v-btn variant="tonal" color="secondary" :data-cy="`${props.dataCy}Cancel`" @click="cancel">Cancel</v-btn>
                        <v-btn variant="flat" color="primary" :disabled="!canApply" :data-cy="`${props.dataCy}Ok`" @click="apply">OK</v-btn>
                    </v-card-actions>
                </v-card>
            </v-menu>
        </template>
    </v-text-field>
</template>

<style scoped>
/* Three independently scrolling columns, tall enough to show a few neighbours either side. */
.bn-time-column {
    flex: 1 1 0;
    max-height: 220px;
    overflow-y: auto;
}
</style>
